import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const TypingText: React.FC<{ texts: string[]; speed?: number }> = ({ texts, speed = 100 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentText = texts[currentTextIndex];
    
    if (!isDeleting && currentCharIndex < currentText.length) {
      // Typing forward
      const timeout = setTimeout(() => {
        setDisplayedText(currentText.substring(0, currentCharIndex + 1));
        setCurrentCharIndex(prev => prev + 1);
      }, speed);

      return () => clearTimeout(timeout);
    } else if (!isDeleting && currentCharIndex === currentText.length) {
      // Finished typing, wait before deleting
      const timeout = setTimeout(() => {
        setIsDeleting(true);
      }, 2000); // Wait 2 seconds before deleting

      return () => clearTimeout(timeout);
    } else if (isDeleting && currentCharIndex > 0) {
      // Deleting
      const timeout = setTimeout(() => {
        setDisplayedText(currentText.substring(0, currentCharIndex - 1));
        setCurrentCharIndex(prev => prev - 1);
      }, speed / 2); // Delete faster

      return () => clearTimeout(timeout);
    } else if (isDeleting && currentCharIndex === 0) {
      // Finished deleting, move to next text
      setIsDeleting(false);
      setCurrentTextIndex(prev => (prev + 1) % texts.length);
    }
  }, [currentCharIndex, currentTextIndex, isDeleting, texts, speed]);

  return <span>{displayedText}</span>;
};

const Hero: React.FC = () => {
  return (
    <section id="hero" className="min-h-screen flex items-center relative overflow-hidden pt-24 md:pt-32">
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center md:text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-6"
            >
              <div className="text-sm md:text-base font-normal text-gray-600 mb-2">Hello! I'am</div>
              <h1 className="text-4xl md:text-6xl font-bold">
                <span className="relative">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">Rishit</span>
                  <motion.div
                    className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-pink-500/20 rounded-lg blur-xl"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </span>
              </h1>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-2xl md:text-3xl mb-8"
            >
              <span className="relative">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
                  <TypingText 
                    texts={[
                      "Data Scientist & AI/ML Engineer",
                      "Multimodal Explorer",
                      "Graduate Researcher",
                      "Impact Focused",
                      "Team Player",
                      "AI/ML Enthusiast",
                      "Systems Thinker",
                      "Experiment-Driven",
                      "Scalable by Design"
                    ]} 
                    speed={80} 
                  />
                </span>
                <motion.span
                  className="inline-block w-0.5 h-6 md:h-8 bg-gradient-to-b from-blue-500 to-purple-500 ml-1"
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-gray-600 mb-8 max-w-lg mx-auto md:mx-0"
            >
              Master's student in Data Science at NYU with expertise in Machine Learning, Deep Learning, and Generative AI. I've built scalable ML pipelines, optimized LLM systems, and developed production-ready AI solutions. Passionate about turning complex data into actionable insights and deploying AI systems that make a real impact.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-wrap gap-4 justify-center md:justify-start"
            >
              <motion.a
                whileHover={{ scale: 1.05, boxShadow: '0 10px 25px rgba(59, 130, 246, 0.4)' }}
                whileTap={{ scale: 0.98 }}
                href="#contact"
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200 flex items-center gap-2 h-[51px] w-[237px] justify-center"
              >
                Get In Touch
                <motion.svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  animate={{ x: [0, 5, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                </motion.svg>
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.05, boxShadow: '0 5px 15px rgba(59, 130, 246, 0.2)' }}
                whileTap={{ scale: 0.98 }}
                href="#projects"
                className="px-6 py-3 bg-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500 border border-blue-500/20 rounded-lg font-medium hover:bg-gradient-to-r hover:from-blue-500/5 hover:to-blue-800/100 transition-colors duration-200 flex items-center gap-2 h-[51px] w-[237px] justify-center"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                </svg>
                <span className="text-center w-full">View Projects</span>
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.05, boxShadow: '0 5px 15px rgba(0, 119, 181, 0.3)' }}
                whileTap={{ scale: 0.98 }}
                href="https://www.linkedin.com/in/rishitm/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-[#0077B5]/10 text-[#0077B5] border border-[#0077B5]/20 rounded-lg font-medium hover:bg-[#0077B5]/20 transition-colors duration-200 flex items-center gap-2 h-[51px] w-[237px] justify-center"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                Connect on LinkedIn
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.05, boxShadow: '0 5px 15px rgba(66, 133, 244, 0.3)' }}
                whileTap={{ scale: 0.98 }}
                href="https://scholar.google.com/citations?user=OVVuSE0AAAAJ&hl=en&oi=sra"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-[#4285F4]/10 text-[#4285F4] border border-[#4285F4]/20 rounded-lg font-medium hover:bg-[#4285F4]/20 transition-colors duration-200 flex items-center gap-2 h-[51px] w-[237px] justify-center"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5.242 13.769L0 9.5 12 0l12 9.5-5.242 4.269C17.548 11.249 14.978 9.5 12 9.5c-2.977 0-5.548 1.748-6.758 4.269zM12 10a7 7 0 1 0 0 14 7 7 0 0 0 0-14z"/>
                </svg>
                Google Scholar
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.05, boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)' }}
                whileTap={{ scale: 0.98 }}
                href="https://github.com/Reemostat"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-[#1C1C1C]/10 text-gray-800 border border-gray-800/20 rounded-lg font-medium hover:bg-[#1C1C1C]/20 transition-colors duration-200 flex items-center gap-2 h-[51px] w-[237px] justify-center"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
                </svg>
                View GitHub
              </motion.a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative z-10">
              <motion.div
                animate={{
                  y: [0, -12, 0],
                  scale: [1, 1.02, 1],
                }}
                transition={{
                  duration: 3.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="bg-white rounded-xl p-5 border border-gray-200 shadow-2xl"
                style={{
                  filter: "drop-shadow(0 10px 25px rgba(0, 0, 0, 0.1))"
                }}
              >
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">Core Values</h3>
                  <div className="grid grid-cols-2 gap-4">
                      {/* Pair 1 */}
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-primary-color/5 flex items-center justify-center">
                          <svg className="w-5 h-5 text-primary-color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">Multimodal Explorer</h3>
                          <p className="text-sm text-gray-500">Text + Signal Synergy</p>
                        </div>
                      </div>

                      {/* Pair 2 */}
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-primary-color/5 flex items-center justify-center">
                          <svg className="w-5 h-5 text-primary-color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">Graduate Researcher</h3>
                          <p className="text-sm text-gray-500">Theory Meets Practice</p>
                        </div>
                      </div>

                      {/* Pair 3 */}
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-primary-color/5 flex items-center justify-center">
                          <svg className="w-5 h-5 text-primary-color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">Impact Focused</h3>
                          <p className="text-sm text-gray-500">Build What Matters</p>
                        </div>
                      </div>

                      {/* Pair 4 */}
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-primary-color/5 flex items-center justify-center">
                          <svg className="w-5 h-5 text-primary-color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">Team Player</h3>
                          <p className="text-sm text-gray-500">Collaborative Edge</p>
                        </div>
                      </div>

                      {/* Pair 5 */}
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-primary-color/5 flex items-center justify-center">
                          <svg className="w-5 h-5 text-primary-color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">AI/ML Enthusiast</h3>
                          <p className="text-sm text-gray-500">Curiosity Powered</p>
                        </div>
                      </div>

                      {/* Pair 6 */}
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-primary-color/5 flex items-center justify-center">
                          <svg className="w-5 h-5 text-primary-color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">Systems Thinker</h3>
                          <p className="text-sm text-gray-500">Architecture Aware</p>
                        </div>
                      </div>

                      {/* Pair 7 */}
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-primary-color/5 flex items-center justify-center">
                          <svg className="w-5 h-5 text-primary-color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">Experiment-Driven</h3>
                          <p className="text-sm text-gray-500">Hypothesis to Iteration</p>
                        </div>
                      </div>

                      {/* Pair 8 */}
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-primary-color/5 flex items-center justify-center">
                          <svg className="w-5 h-5 text-primary-color" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">Scalable by Design</h3>
                          <p className="text-sm text-gray-500">Growth Ready Systems</p>
                        </div>
                      </div>
                    </div>
                  </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;